import { put, takeLatest, select } from 'redux-saga/effects';
import objectiveApi from 'services/objectivesApi';
import notificationApi from 'services/notificationApi';
import userApi from 'services/userApi';
import taskApi from 'services/taskApi';
import { StorageKey, QueryParam, HeaderLeftTabs } from 'utils/constants';
import storage from '@xto10x/storage/lib';
import { delay, getValueFromQueryParam } from '@xto10x/core/lib/app.utils';
import {
  isExecutionCycle,
  isStrategicCycle,
  isUnboundedCycle,
  isCustomRangeMode,
  getCustomRangeDefaults,
  hasOKRModuleAccess
} from 'utils/app.utils';
import analytics from '@xto10x/core/lib/analytics';
import { getObjectiveCyclesSelector } from 'containers/Header/store/reducer';
import { getActiveObjectiveCycleIndex } from 'utils/objectives.utils';
import {
  actions,
  saveObjectiveCycleList,
  saveSelectedObjectiveCycle,
  saveSingleNotification,
  saveNotification,
  setNotificationLoading,
  saveTasks,
  getTasksForCycle,
  markNotificationAsRead
} from './actions';
import {
  showAppLoading,
  hideAppLoading,
  showAlertMessage,
  updateIsAnnualGoalsSpace
} from '../../App/store/actions';
import {
  getIsAdvancedUser,
  getIsBasicUser,
  getAccessibleModules
} from '../../App/store/reducer';
import { fetchObjectiveComments } from '../../ObjectiveActivity/store/actions';
import { getSelectedObjectiveSelector } from '../../Objective/store/reducer';
import objectivesApi from '../../../services/objectivesApi';
import { updateObjective } from '../../Objective/store/actions';

function* fetchOrgObjectiveCyclesWorker({ selectedObjCycleId }) {
  try {
    yield put(showAppLoading());
    const objectiveCycles = yield objectiveApi
      .getObjectiveCycles()
      .then(res => res.data.objective_cycles);
    const executionCycles = objectiveCycles.filter(c =>
      isExecutionCycle(c.cycle_type)
    );
    /* includes both okr and annual cycles */
    const nonUnboundedCycles = objectiveCycles.filter(
      c => !isUnboundedCycle(c.cycle_type)
    );
    const isAdvancedUser = yield select(getIsAdvancedUser);
    const isBasicUser = yield select(getIsBasicUser);
    const orgDomain = storage.getDataFromLocal(StorageKey.ORG_INFO)?.sub_domain;

    if (isAdvancedUser) {
      let cycleList = selectedObjCycleId ? nonUnboundedCycles : executionCycles;
      // Get activeObjectiveCycleIndex to save in reducer and mark it as active.
      let activeObjectiveCycleIndex = getActiveObjectiveCycleIndex(
        cycleList,
        selectedObjCycleId
      );
      /* below check works when there is an invalid cycle id in the params */
      if (activeObjectiveCycleIndex === -1) {
        cycleList = executionCycles;
        activeObjectiveCycleIndex = getActiveObjectiveCycleIndex(
          cycleList,
          null
        );
      }
      /* below check works when there is no active okr cycle */
      if (activeObjectiveCycleIndex === -1 && executionCycles.length > 0) {
        cycleList = executionCycles;
        activeObjectiveCycleIndex = cycleList.length - 1;
      }
      /* this is to map the active index in the objectiveCycles array from local cycleList */
      activeObjectiveCycleIndex = objectiveCycles.findIndex(
        c => c?.id === cycleList[activeObjectiveCycleIndex].id
      );
      const isStrategic = isStrategicCycle(
        objectiveCycles[activeObjectiveCycleIndex].cycle_type
      );
      if (activeObjectiveCycleIndex === -1)
        throw new Error('No valid objective cycle present.');
      objectiveCycles[activeObjectiveCycleIndex].active = true;
      if (isStrategic) yield put(updateIsAnnualGoalsSpace(true));
      if (!isBasicUser || (isBasicUser && !isCustomRangeMode()))
        yield put(
          saveSelectedObjectiveCycle(objectiveCycles[activeObjectiveCycleIndex])
        );
    }
    if (
      !isAdvancedUser &&
      isBasicUser &&
      window.location.pathname.split('/')[2] === 'objectives'
    ) {
      const rangeDefaults = getCustomRangeDefaults();
      const existingStartDate = getValueFromQueryParam(
        QueryParam.CUSTOM_DATE_RANGE_START_DATE
      );
      const existingEndDate = getValueFromQueryParam(
        QueryParam.CUSTOM_DATE_RANGE_END_DATE
      );
      const startDate = existingStartDate || rangeDefaults.csd;
      const endDate = existingEndDate || rangeDefaults.ced;
      const ob = getValueFromQueryParam(QueryParam.SELECTED_OBJECTIVE);
      const tm = getValueFromQueryParam(QueryParam.SELECTED_TEAM);
      window.history.replaceState(
        null,
        '',
        `/${orgDomain}/objectives?tm=${tm || HeaderLeftTabs[0].tag_id}&${
          QueryParam.CUSTOM_DATE_RANGE_START_DATE
        }=${startDate}&${QueryParam.CUSTOM_DATE_RANGE_END_DATE}=${endDate}${
          ob ? `&${QueryParam.SELECTED_OBJECTIVE}=${ob}` : ''
        }`
      );
    }
    yield put(saveObjectiveCycleList(objectiveCycles));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_org_objective_cycles_worker', e);
  }
}

function* setCycleOrCustomRangeDetailsWorker({ selectedObjCycleId }) {
  try {
    const objectiveCycles = yield select(getObjectiveCyclesSelector);
    if (!objectiveCycles || objectiveCycles.length === 0) return;
    const executionCycles = objectiveCycles.filter(c =>
      isExecutionCycle(c.cycle_type)
    );
    /* includes both okr and annual cycles */
    const nonUnboundedCycles = objectiveCycles.filter(
      c => !isUnboundedCycle(c.cycle_type)
    );
    const isAdvancedUser = yield select(getIsAdvancedUser);
    const isBasicUser = yield select(getIsBasicUser);

    if (isAdvancedUser) {
      let cycleList = selectedObjCycleId ? nonUnboundedCycles : executionCycles;
      // Get activeObjectiveCycleIndex to save in reducer and mark it as active.
      let activeObjectiveCycleIndex = getActiveObjectiveCycleIndex(
        cycleList,
        selectedObjCycleId
      );
      /* below check works when there is an invalid cycle id in the params */
      if (activeObjectiveCycleIndex === -1) {
        cycleList = executionCycles;
        activeObjectiveCycleIndex = getActiveObjectiveCycleIndex(
          cycleList,
          null
        );
      }
      /* below check works when there is no active okr cycle */
      if (activeObjectiveCycleIndex === -1 && executionCycles.length > 0) {
        cycleList = executionCycles;
        activeObjectiveCycleIndex = cycleList.length - 1;
      }
      /* this is to map the active index in the objectiveCycles array from local cycleList */
      activeObjectiveCycleIndex = objectiveCycles.findIndex(
        c => c.id === cycleList[activeObjectiveCycleIndex].id
      );
      if (activeObjectiveCycleIndex === -1)
        throw new Error('No valid objective cycle present.');
      objectiveCycles[activeObjectiveCycleIndex].active = true;
      if (
        isStrategicCycle(objectiveCycles[activeObjectiveCycleIndex].cycle_type)
      )
        yield put(updateIsAnnualGoalsSpace(true));
      if (!isCustomRangeMode())
        yield put(
          saveSelectedObjectiveCycle(objectiveCycles[activeObjectiveCycleIndex])
        );
    }
    if (
      !isAdvancedUser &&
      isBasicUser &&
      !isCustomRangeMode() &&
      window.location.pathname.split('/')[2] === 'objectives'
    ) {
      const orgDomain = storage.getDataFromLocal(StorageKey.ORG_INFO)
        ?.sub_domain;
      const rangeDefaults = getCustomRangeDefaults();
      const startDate = rangeDefaults.csd;
      const endDate = rangeDefaults.ced;
      window.history.replaceState(
        null,
        '',
        `/${orgDomain}/objectives?tm=${HeaderLeftTabs[0].tag_id}&${QueryParam.CUSTOM_DATE_RANGE_START_DATE}=${startDate}&${QueryParam.CUSTOM_DATE_RANGE_END_DATE}=${endDate}`
      );
    }
    yield put(saveObjectiveCycleList(objectiveCycles));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('set_cycle_or_custom_range_details_worker', e);
  }
}

function* setObjectiveCycleByIdWorker({ id }) {
  try {
    const cycles = yield select(getObjectiveCyclesSelector);
    yield put(saveSelectedObjectiveCycle(cycles.find(c => c.id === id)));
  } catch (err) {
    yield put(showAlertMessage('error', err.message));
    analytics.trackError('set_objective_cycle_by_id_worker', err);
  }
}

function* fetchNotificationByIdWorker(action) {
  try {
    yield put(setNotificationLoading(true));
    const { data } = yield notificationApi.getNotificationById(
      action.notificationId
    );
    yield put(saveSingleNotification(data));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch__single_notification_worker', e);
  }
}

function* fetchNotificationsWorker(requestBody) {
  try {
    yield put(setNotificationLoading(true));
    const { data } = yield notificationApi.getNotifications(
      requestBody.requestBody
    );
    yield delay();
    yield put(saveNotification(data, requestBody.isOtherAction));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_notifications_worker', e);
  }
}

function* markNotificationReadWorker({ notificationId, module }) {
  try {
    yield put(showAppLoading());
    yield notificationApi.markNotificationRead(notificationId, module);
    yield put(markNotificationAsRead(notificationId));
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('mark_notification_read_worker', e);
  }
}

function* markAllNotificationReadFromWorker({ requestBody }) {
  const _requestBody = {
    limit: 10,
    page: 1
  };
  try {
    yield notificationApi.markAllNotificationReadFrom(requestBody);
    const { data } = yield notificationApi.getNotifications(_requestBody);
    yield put(saveNotification(data, true));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('mark_all_notification_read_from_worker', e);
  }
}

function* sendHelpQueryWorker({ requestBody, successMessage }) {
  try {
    yield userApi.sendHelpQuery(requestBody);
    yield put(
      showAlertMessage('success', successMessage || 'Query sent successfully.')
    );
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('send_help_query_worker', e);
  }
}

function* fetchTasksWorker({ oc }) {
  try {
    const accessibleModules = yield select(getAccessibleModules);
    if (!hasOKRModuleAccess(accessibleModules)) return;
    const taskAssignedByUser = yield taskApi
      .getTaskAssignedByUser(oc)
      .then(res => res.data);
    const taskAssignedToUser = yield taskApi
      .getTaskAssignedToUser(oc)
      .then(res => res.data);
    yield put(saveTasks({ taskAssignedByUser, taskAssignedToUser }));
  } catch (e) {
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('fetch_tasks_worker', e);
  }
}

function* updateTaskStatusWorker({ task_id, status, callback }) {
  try {
    yield put(showAppLoading());
    const { data } = yield taskApi.updateTaskStatus(task_id, status);
    const { data: objectiveData } = yield objectivesApi.getObjectiveDetail(
      data.attributes.objective_id
    );
    const selectedObjective = yield select(getSelectedObjectiveSelector);
    if (selectedObjective) {
      yield put(fetchObjectiveComments(data.attributes.objective_id));
    } else yield put(getTasksForCycle());
    yield put(updateObjective(objectiveData));
    yield put(
      showAlertMessage(
        'success',
        `${
          status === 'OPEN'
            ? 'Action item status updated successfully'
            : 'Action item resolved successfully'
        }`
      )
    );
    if (callback) callback(data);
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('update_tasks_status_worker', e);
  }
}

export default function* defaultSaga() {
  yield takeLatest(
    actions.FETCH_OBJECTIVE_CYCLE_LIST,
    fetchOrgObjectiveCyclesWorker
  );
  yield takeLatest(
    actions.SET_CYCLE_OR_CUSTOM_RANGE_DETAILS,
    setCycleOrCustomRangeDetailsWorker
  );
  yield takeLatest(
    actions.SAVE_SELECTED_OBJECTIVE_CYCLE_BY_ID,
    setObjectiveCycleByIdWorker
  );
  yield takeLatest(actions.GET_NOTIFICATION_BY_ID, fetchNotificationByIdWorker);
  yield takeLatest(actions.GET_ALL_NOTIFICATION, fetchNotificationsWorker);
  yield takeLatest(actions.MARK_NOTIFICATION_READ, markNotificationReadWorker);
  yield takeLatest(
    actions.MARK_ALL_NOTIFICATION_READ_FROM,
    markAllNotificationReadFromWorker
  );
  yield takeLatest(actions.SEND_HELP_QUERY, sendHelpQueryWorker);
  yield takeLatest(actions.GET_TASKS_FOR_CYCLE_ACTION, fetchTasksWorker);
  yield takeLatest(actions.UPDATE_TASK_STATUS_ACTION, updateTaskStatusWorker);
}
